import React, { useEffect, useRef, useState } from 'react';
import { Skeleton, Dropdown } from 'antd';
import { isEqual } from 'lodash';

import './LoanBuilder.scss';
import { useOnboarding } from '../../context/OnboardingContext';

import { LoanOption } from '../../../../shared/components/ui/atoms';
import { FooterContent } from '../../../onboarding/components';
import { SendLoanFormInner } from '../SendLoanForm/SendLoanForm';

import {
  isShortTermFixAndFlip,
  isLongTermRental,
  isRefinance,
  cannotBePriced
} from '../../../../utils/functions';
import { InfoBox } from '../LoanResume/LoanResume';

import { trackSectionLoadedEvent } from '../../../../utils/mixpanel';

import { isProduction } from '../../../../utils/constants';

// LoanBuilder imports
import { defaultValues, PROGRAMS, DownArrowSVG } from './constants';
import { getInputData, isBelowMinLoanAmount, getErrors, getOptionRentalData, getOptionShortTermData } from './utils';
import { LoanBuilderInput, LoanDetails, ARVLTVSlider, MobileLoanBuilder, LoanOptionsAvailable, InfoTooltip, InfoTooltipPositioner } from './components';
import { useLoanComputation } from './hooks';
import { useDropdownScrollLock, SCROLL_FEEDBACK_CLASS } from '../../../../shared/components/ui/DropdownScroll';

interface LoanBuilderProps {
  values: any;
  setValues: (values: any) => void;
  setValue: (key: string, value: any) => void;
  onSelect: (option: any) => void;
  isBrokerMode?: boolean;
  isProcessingTeamMode?: boolean;
}

export const LoanBuilder = React.memo((props: LoanBuilderProps) => {
  // Obtener formValues directamente del contexto de Onboarding en lugar de usar values de props
  const { formValues: values } = useOnboarding();
  
  const { setValues, setValue, onSelect, isBrokerMode, isProcessingTeamMode } = props;

  const [selectedProgram, setSelectedProgram] = useState(PROGRAMS[0])
  const [isProgramDropdownOpen, setIsProgramDropdownOpen] = useState(false)

  useDropdownScrollLock(isProgramDropdownOpen)

  useEffect(() => {
    trackSectionLoadedEvent('Loan Builder')
  }, [])

  const {
    loanOptions,
    multipleLoanOptions,
    isLoading,
    loanBalance,
    setLoanBalance,
    maxLoanBalance,
    min_ltv,
    minLTV,
    max_ltv,
    maxLTV,
    ltv,
    setLtv,
    coreValuesHaveChanged,
  } = useLoanComputation({ values, setValues, selectedProgram, PROGRAMS })
  
  const { loanBalance: _, ...coreValues } = values

  const isRental = isLongTermRental(values)

  const options = (
    (!isRental && loanOptions) || (isRental && multipleLoanOptions && multipleLoanOptions.length > 0)
      ?
      (
        isRental ?
          [2, 1, 0]
            .map((i, idx) => getOptionRentalData(multipleLoanOptions?.[i], [2, 1, 0][idx], idx === 0 ? "LOWEST RATE" : (idx === 2 ? "LOWEST CASH TO CLOSE" : "")))
            .filter(Boolean)
          :
          [2, 1, 0]
            .map((i, idx) => getOptionShortTermData(multipleLoanOptions?.[i], [2, 1, 0][idx], idx === 0 ? "LOWEST RATE" : (idx === 2 ? "LOWEST CASH TO CLOSE" : "")))
            .filter(Boolean)
      )
      :
      []
  )

  /* Default input values */
  const inputData = React.useMemo(() => getInputData(coreValues), [coreValuesHaveChanged])
  const lastInputData = useRef(null)
  const lastValuesRef = useRef<any>(null)
  const currentValuesRef = useRef(values)
  useEffect(() => {
    // Update ref with current values
    currentValuesRef.current = values

    const isFirstCall = !lastInputData.current
    const hasChanged = !isEqual(inputData, lastInputData.current)
    if (isFirstCall || (!isFirstCall && hasChanged)) {
      const defaults = inputData.reduce((acc, { customKey }) => ({ ...acc, [customKey]: defaultValues[customKey as keyof typeof defaultValues] }), {})
      // Merge defaults with current values, but only update if there are actual changes
      const newValues = { ...defaults, ...currentValuesRef.current }
      // Only update if the merged values are different from last processed values to avoid infinite loops
      if (!isEqual(newValues, lastValuesRef.current)) {
        setValues(newValues)
        lastValuesRef.current = newValues
      }
    }
    lastInputData.current = inputData as any
  }, [inputData, setValues])

  const errors = inputData.map((v) => getErrors({ ...v, key: v.customKey, values, loanAmount: maxLoanBalance || 0 }))
  const hasError = errors.some((v) => v !== undefined)

  const isPricerDisabled = cannotBePriced(values) && !isProcessingTeamMode

  /* Input Element - ARV LTV Slider */
  const [maxLTVPosition, setMaxLTVPosition] = useState<string | null>(null)

  useEffect(() => {
    setMaxLTVPosition(null)
  }, [max_ltv])

  const aRequestToTheServerHasBeenMade = multipleLoanOptions !== undefined
  const thereIsAFaultyServerResponse = multipleLoanOptions !== undefined && loanOptions === null
  const isRefreshing = isLoading || coreValuesHaveChanged
  
  // Calcular loanCantBeAutopriced en un useState que se actualiza cuando isRefreshing cambia
  const [loanCantBeAutopriced, setLoanCantBeAutopriced] = useState(() => {
    return !isRefreshing && aRequestToTheServerHasBeenMade && (thereIsAFaultyServerResponse || ((loanOptions && options.length === 0) || hasError || !(loanOptions && (loanOptions as any).monthly_payment) || !loanOptions)) || isPricerDisabled || isBelowMinLoanAmount(loanBalance, values)
  })

  // Actualizar loanCantBeAutopriced cuando cambien sus dependencias, especialmente isRefreshing
  useEffect(() => {
    const newLoanCantBeAutopriced = !isRefreshing && aRequestToTheServerHasBeenMade && (thereIsAFaultyServerResponse || ((loanOptions && options.length === 0) || hasError || !(loanOptions && (loanOptions as any).monthly_payment) || !loanOptions)) || isPricerDisabled || isBelowMinLoanAmount(loanBalance, values)
    setLoanCantBeAutopriced(newLoanCantBeAutopriced)
  }, [isRefreshing, aRequestToTheServerHasBeenMade, thereIsAFaultyServerResponse, loanOptions, options.length, hasError, isPricerDisabled, loanBalance, values])

  /* Input Elements */
  const DSCRElement = ({ dscr }: { dscr: number }) => {
    const text = "A ratio of your rental income to PITI (costs associated with running a rental). Affects the max LTV we can give."
    return (
      <div className='dscr'>DSCR: <span>{dscr ? (dscr).toFixed(2) : "___"}</span><InfoTooltipPositioner><InfoTooltip title={text} placement={"top"} isDisabled={isPricerDisabled} variant="gray" /></InfoTooltipPositioner></div>
    )
  }

  // Build elements array declaratively instead of using splice
  const elements = React.useMemo(() => {
    const baseElements = inputData.map((v) => <LoanBuilderInput  {...v} key={v.customKey} values={values} setValue={setValue} isDisabled={false} loanAmount={loanBalance} />)
    const result: React.ReactNode[] = []

    // Insert base elements with conditional inserts at the correct positions
    baseElements.forEach((element, index) => {
      result.push(element)

      // Insert DSCR element after first element (index 0) for purchase, or after second element (index 1) for refinance
      if (isLongTermRental(values) && index === (isRefinance(values) ? 1 : 0)) {
        const dscr = (loanOptions as any)?.dscr
        result.push(<DSCRElement key="dscr-element" dscr={dscr} />)
      }

      // Insert ARV LTV Slider after third element (index 2) for purchase, or after fourth element (index 3) for refinance
      if (isShortTermFixAndFlip(values) && ltv && !(isPricerDisabled || loanCantBeAutopriced) && !isLoading) {
        if (index === (isRefinance(values) ? 3 : 2)) {
          result.push(<div key="arv-ltv-slider"><ARVLTVSlider ltv={ltv} min_ltv={minLTV || 0} max_ltv={maxLTV || 0} setLtv={setLtv} maxLTVPosition={maxLTVPosition} setMaxLTVPosition={setMaxLTVPosition} /></div>)
        }
      }
    })

    return result
  }, [inputData, values, setValue, isPricerDisabled, loanBalance, loanOptions, ltv, loanCantBeAutopriced, isLoading, maxLTVPosition, maxLTV])

  const getLoanErrors = () => {
    return [
      ...errors.filter(Boolean)
    ]
  }

  const InputSkeleton = () => (<Skeleton.Input style={{ display: 'block', width: '100%', marginBottom: "25px", height: "92px", borderRadius: "10px" }} />)

  const className = isPricerDisabled ? 'loan-builder disabled' : 'loan-builder'
  
  return (
    <React.Fragment>
      <div className={className}>
        <div className='loan-rate-section'>
          <div className='loan-rate'>
            {
              isProcessingTeamMode ?
                <h1>Estimate borrower rate</h1>
                :
                <h1>Estimate your rate</h1>
            }
            <div className='loan-resume'>
              <FooterContent values={values} setValue={setValue} oneLine={true} firstPerson isBrokerMode={isBrokerMode} forceUnlockCreditInfo />
            </div>
            <div className='loan-inputs'>
              {elements}
              <div style={{ height: "20px" }}></div>
            </div>
      {/*       {
              isLongTermRental(values) ?
                <div className='scroll-arrow'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="29" viewBox="0 0 8 29" fill="none">
                    <path d="M3.64645 28.3536C3.84171 28.5488 4.15829 28.5488 4.35355 28.3536L7.53553 25.1716C7.7308 24.9763 7.7308 24.6597 7.53553 24.4645C7.34027 24.2692 7.02369 24.2692 6.82843 24.4645L4 27.2929L1.17157 24.4645C0.976311 24.2692 0.659728 24.2692 0.464466 24.4645C0.269204 24.6597 0.269204 24.9763 0.464466 25.1716L3.64645 28.3536ZM3.5 0L3.5 28H4.5L4.5 0L3.5 0Z" fill="#122536" />
                  </svg>
                </div>
                :
                null
            } */}
          </div>
        </div>
        <div className='loan-options-section'>
          {
            isRental && !isProduction ?
              <div>
                <Dropdown
                  overlayClassName={SCROLL_FEEDBACK_CLASS}
                  onOpenChange={setIsProgramDropdownOpen}
                  menu={{
                    items: PROGRAMS.map(program => ({
                      key: program.key,
                      label: program.label,
                      onClick: () => setSelectedProgram(program)
                    }))
                  }}
                >
                  <div style={{ marginBottom: "18px", width: "300px", cursor: "pointer" }}>
                    <b>Program:</b> {selectedProgram.label} <span style={{ position: 'relative', bottom: '2.75px', left: '2.5px' }}><DownArrowSVG /></span>
                  </div>
                </Dropdown>
              </div>
              :
              null
          }
         {isPricerDisabled || (cannotBePriced(values) && isProcessingTeamMode) ? null : <h1>Loan options</h1>}
          <div className='loan-options-container'>
            {
              isLoading ?
                <React.Fragment>
                  <InputSkeleton />
                  <InputSkeleton /> 
                  <InputSkeleton />
                </React.Fragment>
                :
                loanCantBeAutopriced ?
                  (
                    isPricerDisabled || (cannotBePriced(values) && isProcessingTeamMode) ?
                      <SendLoanFormInner data={{ ...values }} />
                      :
                      <LoanOptionsAvailable errors={getLoanErrors()} />
                  )
                  :
                  (cannotBePriced(values) && isProcessingTeamMode) ?
                    <SendLoanFormInner data={{ ...values }} />
                    :
                    options.map((option, index) => <LoanOption key={JSON.stringify(option)} {...option} rate={(option.rate)} onSelect={() => onSelect(
                      {
                        ...option,
                        nonSelectedOptions: [0, 1, 2].filter(i => i !== index).map(i => options[i])
                      }
                    )} />)
            }
          </div>
          {
            !loanOptions || (isLoading || loanCantBeAutopriced || hasError) || (cannotBePriced(values) && isProcessingTeamMode) ?
              null
              :
              <div className='loan-details-container'>
                <LoanDetails
                  min_ltv={min_ltv}
                  max_ltv={max_ltv}
                  ltv={ltv}
                  setLtv={setLtv}
                  arv_ltv={(loanOptions as any)?.arv_ltv}
                  ltc={(loanOptions as any)?.ltc}
                  isLoading={isLoading}
                  loanBalance={loanBalance}
                  setLoanBalance={setLoanBalance}
                />
              </div>
          }
        </div>
      </div>
      <MobileLoanBuilder
        isLoading={!lastInputData.current || isLoading}
        values={values}
        setValue={setValue}
        isBrokerMode={isBrokerMode}
        inputElements={elements}
        options={options}
        onSelect={onSelect}
        min_ltv={min_ltv}
        max_ltv={max_ltv}
        ltv={ltv}
        setLtv={setLtv}
        arv_ltv={(loanOptions as any)?.arv_ltv}
        ltc={(loanOptions as any)?.ltc}
        loanBalance={loanBalance}
        maxLTVPosition={maxLTVPosition}
        setMaxLTVPosition={setMaxLTVPosition}
        loanCantBeAutopriced={loanCantBeAutopriced}
        isPricerDisabled={isPricerDisabled}
        errors={getLoanErrors()}
      />
    </React.Fragment>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if these specific props change
  return (
    isEqual(prevProps.values, nextProps.values) &&
    prevProps.isBrokerMode === nextProps.isBrokerMode &&
    prevProps.isProcessingTeamMode === nextProps.isProcessingTeamMode
    // Note: setValues, setValue, and onSelect are functions and should be stable
  );
});

LoanBuilder.displayName = 'LoanBuilder';

export const Input = (props: any) => {
  const { label, value, onChange } = props;
  const [innerValue, setInnerValue] = useState(value || "")

  useEffect(() => {
    if (innerValue != value) {
      onChange(innerValue)
    }
  }, [innerValue, value])

  return (
    <div className='loan-input'>
      <p>{label}</p>
      <input value={innerValue} onChange={(event) => setInnerValue(event.target.value)} />
    </div>
  )
}
